import React, { useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { abrirVisor } from "@/lib/visor";
import { catGasto, eur, fechaGasto, asociadoDe } from "@/lib/gastos";
import { Sheet } from "@/pages/Familia";
import { PersonaChip } from "@/components/personas/Avatar";
import { GastoForm } from "./GastoForm";
import { AsignarRapido, AsociarRapido } from "./PorPersona";

export const Asociados = ({ g }) => {
  const data = useData();
  return asociadoDe(g, data).map((a) => a.persona
    ? <PersonaChip key={a.key} persona={a.persona} size="xs" className="text-sm text-[#4A5550]" />
    : <span key={a.key} className="inline-flex items-center gap-1 text-sm text-[#4A5550]">{React.createElement(Icons[a.icon], { size: 14 })} {a.label}</span>);
};

const Dato = ({ label, children }) => (
  <div className="flex justify-between gap-3 py-2.5 border-b border-[#EFEAE1] last:border-0">
    <span className="text-[#6E7A74]">{label}</span><span className="text-right font-semibold text-[#1C2421] min-w-0 break-words">{children}</span>
  </div>
);

export const GastoDetalle = ({ g, onClose, onGuardado }) => {
  const data = useData();
  const { documents, borrarGasto } = data;
  const [editar, setEditar] = useState(false);
  const [borrar, setBorrar] = useState(false);
  const doc = documents.find((d) => d.id === g.documento_id);
  const c = catGasto(g.categoria);
  if (editar) return <GastoForm gasto={g} onClose={() => setEditar(false)} onSaved={(x) => { onClose(); onGuardado?.(x); }} />;
  const eliminar = async (conDoc) => {
    try { await borrarGasto(g.id, conDoc); toast.success(conDoc ? "Gasto eliminado y ticket enviado a la papelera" : "Gasto eliminado"); onClose(); }
    catch { toast.error("No se pudo eliminar el gasto."); }
  };
  return (
    <Sheet onClose={onClose} testid="gasto-detalle">
      <p className="text-sm font-semibold text-[#3A6B56]">{c.label}</p>
      <h2 className="font-heading text-2xl font-bold text-[#1C2421] pr-12">{g.concepto || g.proveedor || c.label}</h2>
      <p className="font-heading text-3xl font-extrabold text-[#1C2421] mt-2" data-testid="gasto-detalle-importe">{eur(g.importe)}</p>
      <div className="mt-4 bg-white border border-[#E5DFD5] rounded-2xl px-4 text-base">
        <Dato label="Fecha">{fechaGasto(g.fecha)}</Dato>
        {!data.familyMembers.some((p) => p.id === g.family_member_id) && !asociadoDe(g, data).length && <Dato label="Familia"><span className="text-[#6E7A74]" data-testid="gasto-detalle-sin-asignar">Sin asignar</span></Dato>}
        {g.proveedor && <Dato label="Establecimiento">{g.proveedor}</Dato>}
        {asociadoDe(g, data).length > 0 && <Dato label="Asociado a"><span className="inline-flex flex-wrap gap-2 justify-end"><Asociados g={g} /></span></Dato>}
        {g.forma_pago && <Dato label="Forma de pago">{g.forma_pago}</Dato>}
        {g.impuestos != null && <Dato label="IVA / impuestos">{eur(g.impuestos)}</Dato>}
        {g.notas && <Dato label="Notas">{g.notas}</Dato>}
      </div>
      {!data.familyMembers.some((p) => p.id === g.family_member_id) && <div className="mt-4"><AsignarRapido g={g} testid="gasto-detalle-asignar" /></div>}
      {!asociadoDe(g, data).some((a) => !a.persona) && <div className="mt-3"><AsociarRapido g={g} testid="gasto-detalle-asociar" /></div>}
      {g.documento_id && (doc
        ? <button data-testid="gasto-detalle-ver-ticket" onClick={() => abrirVisor(doc)} className="mt-4 w-full min-h-[52px] rounded-2xl bg-[#E8F0EC] text-[#2E5645] font-semibold inline-flex items-center justify-center gap-2"><Icons.Paperclip size={18} /> Ver el ticket o factura</button>
        : <p className="mt-4 text-sm text-[#6E7A74]" data-testid="gasto-detalle-ticket-papelera">El justificante está en la papelera de documentos.</p>)}
      {borrar ? (
        <div className="mt-5 bg-[#FEF2F2] rounded-2xl p-4 space-y-2.5" data-testid="gasto-borrar-confirmar">
          <p className="font-semibold text-[#1C2421]">¿Eliminar este gasto?</p>
          {doc && <button data-testid="gasto-borrar-con-ticket" onClick={() => eliminar(true)} className="w-full min-h-[52px] rounded-xl bg-[#B91C1C] text-white font-semibold">Eliminar gasto y enviar el ticket a la papelera</button>}
          <button data-testid="gasto-borrar-solo" onClick={() => eliminar(false)} className={`w-full min-h-[52px] rounded-xl font-semibold ${doc ? "bg-white border border-[#E5DFD5] text-[#1C2421]" : "bg-[#B91C1C] text-white"}`}>{doc ? "Eliminar solo el gasto (conservar el ticket)" : "Eliminar gasto"}</button>
          <button data-testid="gasto-borrar-cancelar" onClick={() => setBorrar(false)} className="w-full min-h-[48px] text-[#4A5550] font-semibold">Cancelar</button>
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-2 gap-2.5">
          <button data-testid="gasto-detalle-editar" onClick={() => setEditar(true)} className="min-h-[52px] rounded-2xl bg-[#3A6B56] text-white font-semibold inline-flex items-center justify-center gap-2"><Icons.Pencil size={18} /> Editar</button>
          <button data-testid="gasto-detalle-eliminar" onClick={() => setBorrar(true)} className="min-h-[52px] rounded-2xl bg-white border border-[#E5DFD5] text-[#B91C1C] font-semibold inline-flex items-center justify-center gap-2"><Icons.Trash2 size={18} /> Eliminar</button>
        </div>
      )}
    </Sheet>
  );
};
